// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Badge, Button, Card, Flex, Group, Loader, Select, Stack, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { Patient } from '@medplum/fhirtypes';
import { DateTimeInput, Form, ResourceInput, useMedplum } from '@medplum/react';
import { resolveDefaultStaffPractitioner } from '@polarissleep/scheduling';
import { IconAlertSquareRounded, IconCircleCheck, IconCirclePlus } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { SlotInfo } from 'react-big-calendar';
import { useNavigate } from 'react-router';
import { getProviderAppEnvConfig } from '../../config/env';
import { getProviderTypeLabels } from '../../constants/appointmentTypes';
import { useActivePractitioners } from '../../hooks/useActivePractitioners';
import { useAppointmentTypes } from '../../hooks/useAppointmentTypes';
import { findProviderAppointments } from '../../scheduling-next/availability-search';
import { bookProviderV2Appointment } from '../../services/booking-service-v2';
import type { AppointmentTypeDefinition } from '../../types/appointmentTypes';
import { createEncounter } from '../../utils/encounter';
import { showErrorNotification } from '../../utils/notifications';
import classes from './CreateVisit.module.css';

interface CreateVisitProps {
  appointmentSlot: SlotInfo | undefined;
}

export function CreateVisit(props: CreateVisitProps): JSX.Element {
  const { appointmentSlot } = props;
  const [formattedDate, setFormattedDate] = useState<string>('');
  const [formattedSlotTime, setFormattedSlotTime] = useState<string>('');
  const [patient, setPatient] = useState<Patient | undefined>();
  const [start, setStart] = useState<Date | undefined>(appointmentSlot?.start);
  const [end, setEnd] = useState<Date | undefined>(appointmentSlot?.end);
  const [isLoading, setIsLoading] = useState(false);
  const [appointmentType, setAppointmentType] = useState<AppointmentTypeDefinition | undefined>();
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string | null>(null);
  const [defaultStaffWarning, setDefaultStaffWarning] = useState<string | undefined>();
  const [resolvingDefaultStaff, setResolvingDefaultStaff] = useState(false);
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { appointmentTypes } = useAppointmentTypes();
  const { practitioners, loading: practitionersLoading, error: practitionersError } = useActivePractitioners();
  const schedulingEngine = getProviderAppEnvConfig().schedulingEngine;

  useEffect(() => {
    if (!appointmentSlot) {
      return;
    }

    const startDate = new Date(appointmentSlot.start);
    const endDate = new Date(appointmentSlot.end);

    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    setFormattedDate(startDate.toLocaleDateString('en-US', options));

    const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
    const startTimeStr = startDate.toLocaleTimeString('en-US', timeOptions);
    const endTimeStr = endDate.toLocaleTimeString('en-US', timeOptions);
    setFormattedSlotTime(`${startTimeStr} – ${endTimeStr}`);
  }, [appointmentSlot]);

  useEffect(() => {
    if (!appointmentType) {
      return;
    }

    let active = true;
    setSelectedPractitionerId(null);
    setDefaultStaffWarning(undefined);
    setResolvingDefaultStaff(true);

    resolveDefaultStaffPractitioner(medplum, appointmentType.coding.code ?? '')
      .then((result) => {
        if (!active) {
          return;
        }

        if (result.status === 'resolved') {
          setSelectedPractitionerId(result.practitionerId);
        } else {
          setDefaultStaffWarning(
            'Default staff is not configured correctly for this appointment type. Select a staff member to continue.'
          );
        }
      })
      .catch(() => {
        if (active) {
          setDefaultStaffWarning(
            'Default staff is not configured correctly for this appointment type. Select a staff member to continue.'
          );
        }
      })
      .finally(() => {
        if (active) {
          setResolvingDefaultStaff(false);
        }
      });

    return () => {
      active = false;
    };
  }, [appointmentType, medplum]);

  function handleAppointmentTypeChange(selectedCode: string | null): void {
    if (!selectedCode) {
      setAppointmentType(undefined);
      setSelectedPractitionerId(null);
      return;
    }

    const apptType = appointmentTypes.find((t) => t.coding.code === selectedCode);
    setAppointmentType(apptType);

    if (apptType && start) {
      setEnd(new Date(start.getTime() + apptType.durationMinutes * 60 * 1000));
    }
  }

  async function handleSubmit(): Promise<void> {
    if (!patient || !start || !end || !selectedPractitionerId || !appointmentType) {
      showNotification({
        color: 'yellow',
        icon: <IconAlertSquareRounded />,
        title: 'Error',
        message: 'Please fill out required fields.',
      });
      return;
    }
    setIsLoading(true);
    try {
      const selectedPractitioner = practitioners.find(
        (practitioner) => practitioner.value === selectedPractitionerId
      )?.practitioner;
      if (!selectedPractitioner) {
        throw new Error('Selected practitioner could not be resolved.');
      }

      let encounterId: string | undefined;
      if (schedulingEngine === 'medplum-v2') {
        const candidates = await findProviderAppointments(medplum, {
          appointmentType,
          practitionerId: selectedPractitionerId,
          startDate: start.toISOString(),
          endDate: end.toISOString(),
        });
        const candidate = candidates.find(
          (available) =>
            available.appointment.start && new Date(available.appointment.start).getTime() === start.getTime()
        );
        if (!candidate) {
          throw new Error('This appointment time is no longer available. Please select another time.');
        }
        const booked = await bookProviderV2Appointment(medplum, { patient, candidate, appointmentType });
        encounterId = booked.encounterId;
      } else {
        const encounter = await createEncounter(medplum, start, end, patient, appointmentType, selectedPractitioner);
        encounterId = encounter.id;
      }
      showNotification({ icon: <IconCircleCheck />, title: 'Success', message: 'Visit created' });
      navigate(`/Patient/${patient.id}/Encounter/${encounterId}`);
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Flex direction="column" gap="md" h="100%" justify="space-between">
        <Stack gap="md" h="100%">
          <Stack gap={0}>
            <Title order={1} fw={500}>
              {formattedDate}
            </Title>
            <Text size="lg">{formattedSlotTime}</Text>
          </Stack>

          <Select
            label="Appointment Type"
            placeholder="Select appointment type"
            data={appointmentTypes.map((t) => ({
              value: t.coding.code ?? '',
              label: t.display,
            }))}
            onChange={handleAppointmentTypeChange}
            clearable
          />

          {appointmentType && (
            <Card withBorder shadow="xs" p="sm">
              <Stack gap="xs">
                <Group gap="xs">
                  <Text size="sm" fw={500}>
                    Duration:
                  </Text>
                  <Text size="sm">{appointmentType.durationMinutes} min</Text>
                </Group>
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" fw={500}>
                    Provider Types:
                  </Text>
                  {getProviderTypeLabels(appointmentType).map(({ role, isPreferred, label }) => (
                    <Badge key={role} variant="light" color={isPreferred ? 'blue' : 'gray'} size="sm">
                      {label}
                    </Badge>
                  ))}
                </Group>
                <Group gap="xs">
                  <Text size="sm" fw={500}>
                    Patient Eligibility:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {appointmentType.patientEligibility}
                  </Text>
                </Group>
              </Stack>
            </Card>
          )}

          {defaultStaffWarning && (
            <Alert color="yellow" title="Staff selection required">
              {defaultStaffWarning}
            </Alert>
          )}

          {practitionersError && (
            <Alert color="red" title="Could not load staff">
              {practitionersError.message}
            </Alert>
          )}

          <Select
            label="Staff"
            placeholder={practitionersLoading || resolvingDefaultStaff ? 'Loading staff…' : 'Select staff'}
            data={practitioners}
            value={selectedPractitionerId}
            onChange={setSelectedPractitionerId}
            disabled={!appointmentType || practitionersLoading || resolvingDefaultStaff}
          />

          {(practitionersLoading || resolvingDefaultStaff) && appointmentType && <Loader size="sm" />}

          <ResourceInput
            label="Patient"
            resourceType="Patient"
            name="Patient-id"
            required
            onChange={(value) => setPatient(value as Patient)}
          />

          <DateTimeInput
            name="start"
            label="Start Time"
            defaultValue={appointmentSlot?.start?.toISOString()}
            required
            onChange={(value) => {
              setStart(new Date(value));
            }}
          />

          <DateTimeInput
            name="end"
            label="End Time"
            defaultValue={end && !isNaN(end.getTime()) ? end.toISOString() : appointmentSlot?.end?.toISOString()}
            required
            onChange={(value) => {
              setEnd(new Date(value));
            }}
            key={appointmentType?.coding.code ?? 'default'}
          />
        </Stack>

        <Button fullWidth mt="xl" type="submit" loading={isLoading} disabled={isLoading}>
          <IconCirclePlus /> <Text ml="xs">Create Visit</Text>
        </Button>
      </Flex>
    </Form>
  );
}
